import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarClock, DraftingCompass, Clapperboard, Play, X, Download,
  Gem, Megaphone, Armchair, Handshake, ArrowLeft, CheckCircle2,
} from "lucide-react";
import { Reveal, Magnetic } from "../lib/motion";
import Navbar from "../components/Navbar";
import StaticBackdrop from "../scene/StaticBackdrop";

export const SCHEDULING_VIDEO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/ytr2rvo3_TUMS%20Relaunch%20Event%202026-06-21.mp4";
export const SCHEDULING_POSTER = "/scheduling-poster.jpg";
export const SCHEDULING_PDF = "/Nexis_Master_Scheduling_Capabilities.pdf";

const CAPABILITIES = [
  {
    icon: CalendarClock,
    num: "01",
    title: "PMP-Driven Master Scheduling & Dependency Mapping",
    desc: "Comprehensive, multi-layered master schedules utilizing critical path methodologies used in mission-critical aerospace programs.",
    bullets: [
      ["Dynamic Bottleneck Isolation", "Chaining your entire asset footprint — staging, tech infrastructure, security, power grids — so timeline shifts automatically re-buffer."],
      ["Run-of-Show Matrixing", "Minute-by-minute tactical execution clocks for live cues, VIP transitions, and vendor handoffs."],
      ["Risk Mitigation Reserve", "Built-in contingency forecasting for weather variations, supply-chain delays, and resource allocations."],
    ],
  },
  {
    icon: DraftingCompass,
    num: "02",
    title: "Spatial Footprint Engineering & Crowd Dynamics",
    desc: "Maximizing the physical geometry of your venue or corporate site to guarantee safety, operational control, and high-end experiential flow.",
    bullets: [
      ["Flow Footprint Optimization", "Strategic positioning of access nodes, registration matrices, and high-velocity transit paths to completely eliminate bottlenecks."],
      ["Logistics Zone Engineering", "Micro-mapping back-of-house operations, heavy technical load-ins, and multi-tier vendor footprints."],
      ["Scalability Prototyping", "Flexible layout matrices that scale effortlessly based on shifting guest metrics or site criteria."],
    ],
  },
  {
    icon: Clapperboard,
    num: "03",
    title: "AI Predictive Modeling & Spatial Concept Simulations",
    desc: "Cutting-edge generative AI engines and spatial rendering software build immersive, high-definition digital twins of your footprint before production builds start.",
    bullets: [
      ["Visual Layout Pre-Simulations", "Cinematic fly-through videos detailing precise spatial depths, sightlines, and structural layouts."],
      ["Executive Vision Calibration", "Transforming a director or stakeholder's raw concepts into a visually unified, highly accurate digital rendering."],
      ["Stakeholder & Compliance Alignment", "Rich multimedia blueprints accelerate board pre-approvals, sponsorship acquisitions, and regulatory permitting."],
    ],
  },
];

const SECTORS = [
  {
    icon: Gem,
    title: "Luxury Weddings & Estates",
    desc: "Preview exact sightlines, vendor load-in zones, staging setups, and multi-day asset flows before setup begins.",
  },
  {
    icon: Megaphone,
    title: "Commercial & Brand Activations",
    desc: "Simulate experiential media pathways, interactive tech installations, and foot-traffic velocity to maximize engagement.",
  },
  {
    icon: Armchair,
    title: "Corporate Suites & Stadium Hospitality",
    desc: "Map out secure VIP arrival checkpoints, precise seating geometry, and micro-timed catering pathways.",
  },
  {
    icon: Handshake,
    title: "Enterprise Suite Hosting",
    desc: "Coordinate high-net-worth crowd dynamics and seamless high-end vendor handoffs with absolute discretion.",
  },
];

const ALLOCATION = [
  ["Planning & Layout Engineering", "$4,500"],
  ["Operations & Hospitality Staffing", "$9,500"],
  ["Site Infrastructure & Rentals", "$3,000"],
  ["Production, Audio & Signage Support", "$5,500"],
  ["Media, Photography & Concept Capture", "$3,000"],
  ["Contingency & Heat-Reserve Funds", "$4,500"],
];

export default function SchedulingPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Master Scheduling & Spatial Engineering — Nexis.team";
    return () => { document.title = "Nexis.team — Building Tomorrow's Enterprise"; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="App noise relative min-h-screen bg-nexis-bg text-white" data-testid="scheduling-page">
      <div className="fixed inset-0 z-0">
        <StaticBackdrop />
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-nexis-bg/10 to-nexis-bg" />
      </div>

      <Navbar />

      <main className="relative z-10">
        {/* ---------- Hero ---------- */}
        <section className="mx-auto max-w-7xl px-6 pb-20 pt-40 md:px-12 md:pb-28">
          <Reveal>
            <button
              onClick={() => navigate("/")}
              className="mb-8 flex items-center gap-2 font-mono text-xs tracking-widest text-white/40 transition-colors hover:text-nexis-blue"
              data-testid="scheduling-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> BACK TO NEXIS.TEAM
            </button>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— PRECISION OPERATIONS PRACTICE</span>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">
              Master Scheduling &<br />
              <span className="text-gradient">Spatial Engineering</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-base leading-relaxed text-white/60">
              At Nexis, we don't just coordinate timelines — we structurally engineer them. Managed by an
              enterprise program executive with deep roots in <span className="text-white/90">NASA Mission Planning</span> and{" "}
              <span className="text-white/90">Federal Healthcare Transformation</span>, our firm brings unmatched mathematical
              precision, dependency tracking, and predictive spatial design to commercial rollouts, high-end
              hospitality suites, and luxury productions.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/60">
              Every vendor asset, logistical bottleneck, and guest pathway is mapped, calculated, and simulated
              digitally long before operations go live.
            </p>
          </Reveal>
        </section>

        {/* ---------- Capabilities ---------- */}
        <section className="border-t border-white/5 bg-nexis-bg/80 py-24 backdrop-blur-sm md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] text-nexis-purple">— HIGH-VALUE CAPABILITIES & DELIVERABLES</span>
              <h2 className="mt-5 max-w-3xl font-display text-3xl font-light tracking-tight lg:text-4xl">
                Blueprint architectures that transition abstract visions into{" "}
                <span className="text-gradient">risk-mitigated physical realities</span>.
              </h2>
            </Reveal>

            <div className="mt-16 space-y-6">
              {CAPABILITIES.map((c, i) => (
                <Reveal key={c.num} delay={i * 0.08}>
                  <div className="glass glass-hover grid gap-8 rounded-3xl p-8 md:grid-cols-[1fr_1.4fr] md:p-10" data-testid={`scheduling-capability-${i}`}>
                    <div>
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-nexis-blue ring-1 ring-white/10">
                          <c.icon className="h-5 w-5" />
                        </div>
                        <span className="font-mono text-xs tracking-[0.3em] text-white/25">{c.num}</span>
                      </div>
                      <h3 className="mt-6 font-display text-xl font-medium tracking-tight md:text-2xl">{c.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/55">{c.desc}</p>
                    </div>
                    <ul className="space-y-4 md:border-l md:border-white/10 md:pl-8">
                      {c.bullets.map(([label, text]) => (
                        <li key={label} className="flex gap-3">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-nexis-purple" />
                          <span className="text-sm leading-relaxed text-white/60">
                            <span className="font-medium text-white/90">{label}:</span> {text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Video: Tactical Blueprint in Action ---------- */}
        <section className="border-t border-white/5 bg-nexis-bg/80 py-24 backdrop-blur-sm md:py-32">
          <div className="orb orb-blue right-[8%] top-[10%] h-[340px] w-[340px]" />
          <div className="mx-auto max-w-6xl px-6 md:px-12">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— TACTICAL BLUEPRINT IN ACTION</span>
              <h2 className="mt-5 max-w-2xl font-display text-3xl font-light tracking-tight lg:text-4xl">
                AI Spatial <span className="text-gradient">Simulation</span>.
              </h2>
              <p className="mt-6 max-w-3xl text-sm leading-relaxed text-white/55">
                To eliminate logistical guesswork, we construct a fully realized digital twin of an environment before
                a single asset is deployed. Below is an engineering proof-of-concept modeled from an event architecture
                framework we designed for a regional healthcare organization relaunch — the Executive Director's abstract
                vision translated into an immersive, data-mapped 3D layout simulation.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <motion.button
                onClick={() => setOpen(true)}
                data-testid="scheduling-video-play"
                className="group relative mt-12 block w-full overflow-hidden rounded-[2rem] glass"
                whileHover={{ scale: 1.005 }}
                transition={{ type: "spring", stiffness: 160, damping: 20 }}
              >
                <div className="relative aspect-video w-full overflow-hidden rounded-[2rem]">
                  <img
                    src={SCHEDULING_POSTER}
                    alt="AI spatial layout pre-simulation"
                    className="h-full w-full object-cover opacity-75 transition-all duration-700 group-hover:scale-105 group-hover:opacity-95"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nexis-bg/80 via-nexis-bg/20 to-transparent" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                    <Magnetic strength={0.35}>
                      <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-nexis-blue/90 text-black shadow-[0_0_50px_rgba(0,200,255,0.5)] transition-transform group-hover:scale-110">
                        <span className="absolute inset-0 rounded-full bg-nexis-blue/40 blur-md" />
                        <Play className="relative ml-1 h-8 w-8 fill-black" />
                      </span>
                    </Magnetic>
                    <span className="rounded-full border border-white/15 bg-black/40 px-5 py-2 font-mono text-xs tracking-widest text-white/80 backdrop-blur">
                      PLAY AI SPATIAL LAYOUT PRE-SIMULATION
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6 flex items-center gap-3 font-mono text-xs text-white/70">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-nexis-blue" /> NEXIS.TEAM
                    </span>
                    <span className="text-white/30">// DIGITAL TWIN PROOF-OF-CONCEPT</span>
                  </div>
                </div>
              </motion.button>
            </Reveal>

            {/* sectors */}
            <Reveal delay={0.1}>
              <h3 className="mt-20 font-display text-2xl font-light tracking-tight">
                How we apply this layout framework <span className="text-gradient">across sectors</span>:
              </h3>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {SECTORS.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.06}>
                  <div className="glass glass-hover h-full rounded-3xl p-7" data-testid={`scheduling-sector-${i}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/5 text-nexis-purple ring-1 ring-white/10">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <h4 className="mt-5 font-display text-lg font-medium tracking-tight">{s.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Capital Allocation ---------- */}
        <section className="border-t border-white/5 bg-nexis-bg/80 py-24 backdrop-blur-sm md:py-32">
          <div className="orb orb-purple left-[5%] top-[20%] h-[320px] w-[320px]" />
          <div className="mx-auto max-w-5xl px-6 md:px-12">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] text-nexis-purple">— PORTFOLIO FRAMEWORK</span>
              <h2 className="mt-5 max-w-2xl font-display text-3xl font-light tracking-tight lg:text-4xl">
                Capital Allocation <span className="text-gradient">Architecture</span>.
              </h2>
              <p className="mt-6 max-w-3xl text-sm leading-relaxed text-white/55">
                Our conceptual packages move far beyond visual assets. We deliver data-backed, boardroom-ready
                documentation designed to pass rigorous financial, risk management, and compliance audits. Below is a
                sample structural blueprint mapping every dollar to targeted operational outcomes.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="glass mt-12 overflow-hidden rounded-3xl" data-testid="scheduling-allocation-table">
                <div className="flex items-center justify-between border-b border-white/10 px-7 py-4">
                  <span className="font-mono text-[11px] tracking-[0.25em] text-nexis-blue">SAMPLE STRUCTURAL BLUEPRINT</span>
                  <span className="font-mono text-[10px] tracking-widest text-white/30">ILLUSTRATIVE FRAMEWORK</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left font-mono text-[11px] tracking-widest text-white/40">
                      <th className="px-7 py-4 font-normal">CATEGORY FRAMEWORK</th>
                      <th className="px-7 py-4 text-right font-normal">ESTIMATED PROJECT ALLOCATION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ALLOCATION.map(([cat, amt]) => (
                      <tr key={cat} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                        <td className="px-7 py-4 text-white/70">{cat}</td>
                        <td className="px-7 py-4 text-right font-mono text-white/85">{amt}</td>
                      </tr>
                    ))}
                    <tr className="bg-white/[0.04]">
                      <td className="px-7 py-5 font-medium text-white">Estimated Baseline Total</td>
                      <td className="px-7 py-5 text-right font-mono text-lg font-semibold text-nexis-blue">$30,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ---------- PDF CTA ---------- */}
        <section className="border-t border-white/5 bg-nexis-bg/80 py-24 backdrop-blur-sm md:py-32">
          <div className="mx-auto max-w-5xl px-6 text-center md:px-12">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] text-nexis-blue">— SYSTEM INTEGRATION ASSET</span>
              <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-light tracking-tight lg:text-4xl">
                Official corporate <span className="text-gradient">capability architecture</span>.
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-white/55">
                This asset outlines our underlying data modeling matrices, cross-sector deployment blueprints, and
                system integration specs for board review.
              </p>
              <div className="mt-10 flex justify-center">
                <Magnetic>
                  <a
                    href={SCHEDULING_PDF}
                    download="Nexis_Master_Scheduling_Capabilities.pdf"
                    className="group flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-black transition-transform hover:scale-[1.02]"
                    data-testid="scheduling-pdf-download"
                  >
                    <Download className="h-4 w-4" />
                    Download Executable Capability Statement (PDF)
                  </a>
                </Magnetic>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ---------- footer strip ---------- */}
        <footer className="relative border-t border-white/10 bg-nexis-bg py-12">
          <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 md:flex-row md:items-center md:px-12">
            <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="scheduling-footer-logo">
              <span className="relative flex h-7 w-7 items-center justify-center">
                <span className="absolute inset-0 rounded-md bg-gradient-to-br from-nexis-blue to-nexis-purple" />
                <span className="relative font-display text-sm font-bold text-black">N</span>
              </span>
              <span className="font-display text-lg font-semibold tracking-tight">
                Nexis<span className="text-nexis-blue">.</span>team
              </span>
            </button>
            <span className="text-xs text-white/35">© {new Date().getFullYear()} Nexis.team — Master Scheduling & Spatial Engineering.</span>
          </div>
        </footer>
      </main>

      {/* video modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 p-4 backdrop-blur-md"
            data-testid="scheduling-video-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 200, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl overflow-hidden rounded-2xl glass"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white/70 backdrop-blur hover:bg-black/70 hover:text-white"
                data-testid="scheduling-video-close"
                aria-label="Close video"
              >
                <X className="h-5 w-5" />
              </button>
              <video
                src={SCHEDULING_VIDEO}
                poster={SCHEDULING_POSTER}
                controls
                autoPlay
                playsInline
                className="aspect-video w-full bg-black"
                data-testid="scheduling-video"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
